<template>
    <span class="nadia-thinking">
        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
        <output class="sr-only" aria-live="polite" aria-atomic="true">{{ announcement }}</output>
        <span class="nadia-mark" aria-hidden="true">
            <component :is="NADIA_MARKS[arrangement]" />
        </span>

        <span class="nadia-text-stack" aria-hidden="true">
            <span v-if="props.statusText" class="nadia-text is-active is-fixed">{{ props.statusText }}</span>
            <template v-else>
                <span v-for="(line, i) in TEXTS" :key="i" class="nadia-text" :class="{ 'is-active': text === i }">{{ line }}</span>
            </template>
        </span>
        <span class="shrink-0"><slot /></span>
    </span>
</template>

<script lang="ts">
let loadIndex = 0;
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { NADIA_MARKS } from "./components/thinking/nadiaMarks";

const props = withDefaults(defineProps<{ statusText?: string; context?: "calendar" | "perspective" | null }>(), {
    statusText: "",
    context: null,
});

const { t } = useI18n();

// Static t() calls, so the catalogue check can see every key is used.
const EXPLORE_PHRASES = [
    () => t("chat.thinking.phrases.explore.ponder"),
    () => t("chat.thinking.phrases.explore.connect"),
    () => t("chat.thinking.phrases.explore.reflect"),
    () => t("chat.thinking.phrases.explore.considerAngles"),
    () => t("chat.thinking.phrases.explore.gatherInsights"),
    () => t("chat.thinking.phrases.explore.thinkThrough"),
    () => t("chat.thinking.phrases.explore.organize"),
    () => t("chat.thinking.phrases.explore.exploreViews"),
];
const COMPOSE_PHRASES = [
    () => t("chat.thinking.phrases.compose.craft"),
    () => t("chat.thinking.phrases.compose.searchWords"),
    () => t("chat.thinking.phrases.compose.synthesize"),
    () => t("chat.thinking.phrases.compose.formulate"),
];
const SOURCE_CAPTIONS = {
    calendar: () => t("chat.thinking.captions.calendar"),
    perspective: () => t("chat.thinking.captions.perspective"),
};

const pickOne = <T,>(items: T[]) => items[crypto.getRandomValues(new Uint32Array(1))[0] % items.length];

// Chosen once per mount so a re-render never reshuffles the sequence mid-wait.
const phrases = [pickOne(EXPLORE_PHRASES), pickOne(COMPOSE_PHRASES)];
const TEXTS = computed(() => [...(props.context ? [SOURCE_CAPTIONS[props.context]] : []), ...phrases].map((line) => line()));
const TEXT_MS = 1250;
const REGION_REGISTRATION_DELAY_MS = 300;

const arrangement = ref(loadIndex % NADIA_MARKS.length);
loadIndex += 1;

const text = ref(0);
const announcement = ref("");
let textTimer: ReturnType<typeof setInterval> | null = null;
let announcementTimeout: ReturnType<typeof setTimeout> | null = null;
let regionRegistered = false;

watch(
    () => props.statusText,
    (statusText) => {
        if (statusText) {
            announcement.value = statusText;
        }
    },
);

const stopRotation = () => {
    if (textTimer !== null) {
        clearInterval(textTimer);
        textTimer = null;
    }
};

const startRotation = () => {
    stopRotation();
    text.value = 0;
    textTimer = setInterval(() => {
        text.value += 1;
        if (text.value >= TEXTS.value.length - 1) {
            stopRotation();
        }
    }, TEXT_MS);
};

// The tool call that names the source usually arrives after the loader has mounted, so the
// sequence restarts from its source caption rather than skipping it. Before the region has
// registered, the delayed first announcement already picks up the new caption.
watch(
    () => props.context,
    () => {
        startRotation();
        if (regionRegistered && !props.statusText) {
            announcement.value = TEXTS.value[0];
        }
    },
);

onMounted(() => {
    startRotation();
    announcementTimeout = setTimeout(() => {
        regionRegistered = true;
        announcement.value = props.statusText || TEXTS.value[0];
    }, REGION_REGISTRATION_DELAY_MS);
});

onUnmounted(() => {
    if (announcementTimeout !== null) {
        clearTimeout(announcementTimeout);
    }
    stopRotation();
});
</script>

<style scoped>
@reference "../../css/tailwind.css";

@layer components {
    .nadia-thinking {
        @apply inline-flex max-w-full items-center gap-3;
    }

    .nadia-mark {
        @apply size-10.5 shrink-0;
        animation: kvFade 360ms ease-out both;
    }

    .nadia-text-stack {
        @apply inline-grid min-w-0;
    }

    .nadia-text {
        @apply body-small text-ink-muted whitespace-nowrap;
        grid-area: 1 / 1;
        opacity: 0;
        transform: translateY(4px);
        transition:
            opacity 220ms ease,
            transform 220ms ease;
    }

    .nadia-text.is-active {
        opacity: 1;
        transform: translateY(0);
        transition:
            opacity 260ms ease 230ms,
            transform 260ms ease 230ms;
    }

    .nadia-text.is-fixed {
        @apply whitespace-normal;
    }
}

@keyframes kvFade {
    0% {
        opacity: 0;
        transform: scale(0.9);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .nadia-mark {
        animation: none;
    }

    .nadia-text,
    .nadia-text.is-active {
        transform: none;
        transition: opacity 200ms ease;
    }
}
</style>
